import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Typography,
  Box,
  Grid,
  Container,
  Paper,
  MenuItem
} from "@mui/material";
import { vendorApi } from "../../api/api";
import FileUploadWithPreview from "../common/FileUploadWithPreview";
import {
  calculateTotalPrice,
  calculateSqftPrice
} from "../../Utils/pricecalculator";

const AddFlat = ({ constructionStatus = "Under Construction" }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef();

  /* ================= BASIC ================= */
  const [apartments, setApartments] = useState([]);
  const [apartmentId, setApartmentId] = useState("");
  const [flatName, setFlatName] = useState("");
  const [floorNo, setFloorNo] = useState("");
  const [flatType, setFlatType] = useState("");
  const [sizeSqft, setSizeSqft] = useState("");
  const [priceType, setPriceType] = useState("Total");
  const [facing, setFacing] = useState("");

  /* ================= PRICE ================= */
  const [pricePerSqFt, setPricePerSqFt] = useState("");
  const [totalPrice, setTotalPrice] = useState("");

  /* ================= IMAGES ================= */
  const [displayImage, setDisplayImage] = useState(null);
  const [displayPreview, setDisplayPreview] = useState(null);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);

  /* ================= UI ================= */
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
const formatINRLacCr = (value) => {
  if (!value) return "";
  const num = Number(value);

  if (num >= 1e7) {
    const cr = num / 1e7;
    return Number.isInteger(cr) ? `${cr} Cr` : `${cr.toFixed(2)} Cr`;
  }

  if (num >= 1e5) {
    const l = num / 1e5;
    return Number.isInteger(l) ? `${l} L` : `${l.toFixed(2)} L`;
  }

  return num.toLocaleString("en-IN"); // less than 1 Lakh
};


  /* ================= FETCH APARTMENTS ================= */
  useEffect(() => {
    const fetchApartments = async () => {
      setLoading(true);
      try {
        const res = await vendorApi.getApartments();
        setApartments(res.data || []);
      } catch (err) {
        console.error("Failed to fetch apartments", err);
      } finally {
        setLoading(false);
      }
    };
    fetchApartments();
  }, []);

  /* ================= PRICE CALCULATION ================= */
  useEffect(() => {
    if (!sizeSqft) return;

    if (priceType === "PerSqFt" && pricePerSqFt) {
      setTotalPrice(calculateTotalPrice(pricePerSqFt, sizeSqft));
    }

    if (priceType === "Total" && totalPrice) {
      setPricePerSqFt(calculateSqftPrice(totalPrice, sizeSqft));
    }
  }, [sizeSqft, priceType]);

  const handleSqftPriceChange = (value) => {
    setPricePerSqFt(value);
    if (sizeSqft) {
      setTotalPrice(calculateTotalPrice(value, sizeSqft));
    }
  };

  const handleTotalPriceChange = (value) => {
    setTotalPrice(value);
    if (sizeSqft) {
      setPricePerSqFt(calculateSqftPrice(value, sizeSqft));
    }
  };

  /* ================= GALLERY ================= */
  const handleGalleryChange = (e) => {
    const selected = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selected]);
    setPreviews((prev) => [
      ...prev,
      ...selected.map((f) => URL.createObjectURL(f))
    ]);
    e.target.value = null;
  };

  /* ================= SAVE ================= */
  const saveFlat = async (e) => {
    e.preventDefault();

    if (!apartmentId || !flatName || !flatType || !displayImage) {
      setSnackbar({
        open: true,
        message: "Please fill all required fields",
        severity: "error"
      });
      return;
    }

    setLoading(true);
    const fd = new FormData();

    fd.append("ApartmentId", apartmentId);
    fd.append("FlatName", flatName);
    fd.append("FloorNo", floorNo);
    fd.append("FlatType", flatType);
    fd.append("SizeInSqFt", sizeSqft);
    fd.append("PriceType", priceType);
    fd.append(
      "Price",
      priceType === "PerSqFt" ? pricePerSqFt : totalPrice
    );
    fd.append("Facing", facing);
    fd.append("DisplayImage", displayImage);

    files.forEach((img) => fd.append("images[]", img));

    try {
      await vendorApi.addFlat(fd);
      setSnackbar({
        open: true,
        message: "Flat added successfully",
        severity: "success"
      });
      setTimeout(() => navigate("/apartment"), 1500);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to add flat",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container sx={{ py: 5 }}>
      <Backdrop open={loading}>
        <CircularProgress />
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" mb={3}>
          Add Flat
        </Typography>

        <form onSubmit={saveFlat}>
          <Grid container spacing={3}>
            {/* Apartment */}
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Select Apartment"
                fullWidth
                value={apartmentId}
                onChange={(e) => setApartmentId(e.target.value)}
              >
                {apartments.map((ap) => (
                  <MenuItem key={ap.id} value={ap.id}>
                    {ap.ApartmentName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Flat Name */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Flat Name / No"
                fullWidth
                value={flatName}
                onChange={(e) => setFlatName(e.target.value)}
              />
            </Grid>

            {/* Floor */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Floor No"
                type="number"
                fullWidth
                value={floorNo}
                onChange={(e) => setFloorNo(e.target.value)}
              />
            </Grid>

            {/* Flat Type */}
            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Flat Type"
                fullWidth
                value={flatType}
                onChange={(e) => setFlatType(e.target.value)}
              >
                <MenuItem value="1BHK">1BHK</MenuItem>
                <MenuItem value="2BHK">2BHK</MenuItem>
                <MenuItem value="3BHK">3BHK</MenuItem>
                <MenuItem value="4BHK">4BHK</MenuItem>
                <MenuItem value="5BHK">5BHK</MenuItem>
                <MenuItem value="6BHK">6BHK</MenuItem>
                <MenuItem value="7BHK">7BHK</MenuItem>
              </TextField>
            </Grid>

            {/* Size */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Buildup Size (SqFt)"
                fullWidth
                value={sizeSqft}
                onChange={(e) => setSizeSqft(e.target.value)}
              />
            </Grid>

            {/* Price Type */}
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Price Type"
                fullWidth
                value={priceType}
                onChange={(e) => setPriceType(e.target.value)}
              >
                <MenuItem value="Total">Total Price</MenuItem>
                <MenuItem value="PerSqFt">Price per SqFt</MenuItem>
              </TextField>
            </Grid>

            {/* Price */}
            <Grid item xs={12} md={6}>
              {priceType === "PerSqFt" ? (
                <TextField
                  label="Price per SqFt"
                  fullWidth
                  value={pricePerSqFt}
                  onChange={(e) =>
                    handleSqftPriceChange(e.target.value)
                  }
                />
              ) : (
                <TextField
                  label="Total Price"
                  fullWidth
                  value={totalPrice}
                  onChange={(e) =>
                    handleTotalPriceChange(e.target.value)
                  }
                />
              )}
            </Grid>
            <Grid item xs={12}>
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      backgroundColor: "#f9fafb",
      borderStyle: "dashed"
    }}
  >
    {priceType === "PerSqFt" ? (
      <Typography variant="subtitle1">
        <strong>Total Price:</strong>{" "}
        {totalPrice ? formatINRLacCr(totalPrice) : "—"}
      </Typography>
    ) : (
      <Typography variant="subtitle1">
        <strong>Price per SqFt:</strong>{" "}
        {pricePerSqFt ? formatINRLacCr(pricePerSqFt) : "—"}
      </Typography>
    )}
  </Paper>
</Grid>


            {/* Facing */}
            <Grid item xs={12}>
              <TextField
                label="Facing"
                fullWidth
                value={facing}
                onChange={(e) => setFacing(e.target.value)}
              />
            </Grid>

            {/* Display Image */}
            <Grid item xs={12}>
              <Typography variant="h6">Display Picture</Typography>
              <Button component="label" variant="outlined">
                Upload Display Image
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    setDisplayImage(e.target.files[0]);
                    setDisplayPreview(
                      URL.createObjectURL(e.target.files[0])
                    );
                  }}
                />
              </Button>
              {displayPreview && (
                <Box mt={2}>
                  <img
                    src={displayPreview}
                    alt="display"
                    width={200}
                    style={{ borderRadius: 8 }}
                  />
                </Box>
              )}
            </Grid>

            {/* Gallery */}
            <Grid item xs={12}>
              <Typography variant="h6">Flat Gallery Images</Typography>
              <FileUploadWithPreview
                handleFileChange={handleGalleryChange}
                previews={previews}
                files={files}
                fileInputRef={fileInputRef}
              />
            </Grid>
          </Grid>

          <Box mt={4} textAlign="right">
            <Button type="submit" variant="contained">
              Save Flat
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default AddFlat;
